import { useEffect, useRef, useState } from "react";
import { getErrorMessageFromUnknown } from "_shared/_errors/api-error-message";
import {
  useRemindProposalReviewerMutation,
  useRequestProposalReReviewMutation,
} from "work/proposals/negotiation/proposal-collaboration-api";
import { buildRequestProposalReviewCommand } from "work/proposals/negotiation/proposal-command-builder";
import type { ProposalCollaborationEnvelope } from "work/proposals/negotiation/proposal-collaboration-contract";
import type { PendingProposalReviewer } from "work/proposals/_views/_shared/proposal-pending-review-warning";

/** Where one reviewer's reminder stands, as the row that offers it draws it. */
export type ProposalReviewerReminderState =
  | { status: "idle" }
  | { status: "sending" }
  | { status: "sent" }
  | { status: "refused"; message: string };

const IDLE: ProposalReviewerReminderState = { status: "idle" };

/**
 * How long a row reads "Reminded just now" before it offers Remind again (#6871). The server sends
 * every reminder it is asked for, so this pause is the only guard against a double click.
 */
export const REMINDER_SENT_DISPLAY_MS = 4000;

/**
 * Reminding the leader's pending reviewers (#6618), from the submit dialog or the Reviewers popover.
 *
 * Which request a reminder is depends on why the reviewer is pending. One who approved an earlier
 * version is asked to re-review: their approval is already not current, so the request dismisses
 * nothing they still have, and it is the command the server records the ask with. One who never
 * reviewed is sent a reminder, which is a notification and nothing else. Either way the row then reads
 * "Reminded just now" for a few seconds, and then offers Remind again. The leader may remind as often
 * as they choose; the server has no throttle (#6871).
 *
 * Neither request touches Submit: reminding is something the leader may do before submitting, not a
 * step submitting waits on (#5128).
 */
export function useRemindProposalReviewers(params: {
  envelope?: ProposalCollaborationEnvelope;
  viewerScope?: string;
}) {
  const { envelope, viewerScope } = params;
  const [remindProposalReviewer] = useRemindProposalReviewerMutation();
  const [requestProposalReReview] = useRequestProposalReReviewMutation();
  const [states, setStates] = useState<ReadonlyMap<string, ProposalReviewerReminderState>>(EMPTY_STATES);
  const sentTimers = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());

  useEffect(() => {
    const timers = sentTimers.current;
    return () => timers.forEach(clearTimeout);
  }, []);

  // The same gate the server asserts: reminding is reviewer administration, the leader's to do.
  const canRemind = Boolean(envelope?.capabilities.canManageReviewers) && Boolean(viewerScope);

  function setState(userId: string, state: ProposalReviewerReminderState) {
    setStates((current) => new Map(current).set(userId, state));
  }

  // Back to Remind, unless the row has moved on since (a later send, or a refusal).
  function clearSent(userId: string, sent: ProposalReviewerReminderState) {
    setStates((current) => {
      if (current.get(userId) !== sent) return current;
      const next = new Map(current);
      next.delete(userId);
      return next;
    });
  }

  function settle(userId: string, state: ProposalReviewerReminderState) {
    setState(userId, state);
    if (state.status !== "sent") return;
    const timer = setTimeout(() => {
      sentTimers.current.delete(timer);
      clearSent(userId, state);
    }, REMINDER_SENT_DISPLAY_MS);
    sentTimers.current.add(timer);
  }

  function remind(reviewer: PendingProposalReviewer) {
    if (!envelope || !viewerScope || !canRemind) return;
    const current = states.get(reviewer.userId)?.status;
    if (current === "sending" || current === "sent") return;

    setState(reviewer.userId, { status: "sending" });
    void sendReminder({
      envelope,
      viewerScope,
      reviewer,
      remindProposalReviewer,
      requestProposalReReview,
    }).then((state) => settle(reviewer.userId, state));
  }

  function remindAll(reviewers: readonly PendingProposalReviewer[]) {
    reviewers.forEach(remind);
  }

  function stateOf(userId: string): ProposalReviewerReminderState {
    return states.get(userId) ?? IDLE;
  }

  return { canRemind, remind, remindAll, stateOf };
}

const EMPTY_STATES: ReadonlyMap<string, ProposalReviewerReminderState> = new Map();

/** Module scope: a `try` inside a component costs it the React Compiler (see app/CLAUDE.md). */
async function sendReminder(params: {
  envelope: ProposalCollaborationEnvelope;
  viewerScope: string;
  reviewer: PendingProposalReviewer;
  remindProposalReviewer: ReturnType<typeof useRemindProposalReviewerMutation>[0];
  requestProposalReReview: ReturnType<typeof useRequestProposalReReviewMutation>[0];
}): Promise<ProposalReviewerReminderState> {
  const { envelope, viewerScope, reviewer } = params;
  const request =
    reviewer.status === "approvedEarlierVersion"
      ? params.requestProposalReReview({
          negotiationId: envelope.negotiationId,
          viewerScope,
          command: buildRequestProposalReviewCommand({
            envelope,
            reviewerUserId: reviewer.userId,
            reviewerLegalEntityId: reviewer.legalEntityId,
          }),
        })
      : params.remindProposalReviewer({
          negotiationId: envelope.negotiationId,
          command: { reviewerUserId: reviewer.userId, reviewerLegalEntityId: reviewer.legalEntityId },
        });
  try {
    await request.unwrap();
    return { status: "sent" };
  } catch (failure) {
    return { status: "refused", message: describeRefusal(failure) };
  }
}

function describeRefusal(failure: unknown): string {
  return getErrorMessageFromUnknown(failure) ?? "The reminder could not be sent. Try again.";
}
